/**
 * Makanly Design System v2.0.0 - Tailwind CSS Preset
 *
 * Apple Aesthetic / Monochrome with Warm Neutrals
 *
 * PRIMARY BRAND IDENTITY:
 * - Primary: Teal (#587B7F) - F&B-appropriate, professional
 * - Neutrals: Warm Grays (#F7F7F6 → #1B1B1E) - Softer, organic feel
 * - Accent: Gold (#E2C044) - Success, rewards, premium features
 * - Typography: Inter (primary font family)
 *
 * CRITICAL: This preset is the single source of truth for all Tailwind theming.
 * All client apps MUST use this preset. Do NOT override colors in individual apps.
 *
 * Import this preset in your tailwind.config.js to get the design system:
 * ```js
 * import { tailwindPreset } from '@makanly/ui-kit';
 * export default {
 *   presets: [tailwindPreset],
 *   content: ['./src/**\/*.{ts,tsx}', '../../libs/ui-kit/src/**\/*.{ts,tsx}'],
 * }
 * ```
 *
 * @see /design/design.json for complete design system specification
 */

import type { Config } from 'tailwindcss';
import { fontFamily, fontSize } from './tokens/typography';
import { spacing, borderRadius, boxShadow } from './tokens/spacing';
import { primary, gray, accent, semantic } from './tokens/colors';

/**
 * Makanly Tailwind CSS Preset v2.0.0
 *
 * This is the CENTRALIZED theme configuration for all Makanly frontend apps.
 * Do NOT override colors or fonts in individual app tailwind configs.
 *
 * Import in your tailwind.config:
 * ```js
 * import tailwindPreset from '@makanly/ui-kit/tailwind-preset';
 * // or
 * import { tailwindPreset } from '@makanly/ui-kit/tailwind-preset';
 * ```
 */
export const tailwindPreset: Partial<Config> = {
  darkMode: ['class'],
  theme: {
    container: {
      center: true,
      padding: '2rem',
      screens: {
        '2xl': '1400px',
      },
    },
    extend: {
      colors: {
        // ========================================
        // Design System v2.0.0 Color Scales
        // Apple Aesthetic / Monochrome with Warm Neutrals
        // ========================================

        // Primary - Teal (brand color)
        primary: {
          50: primary[50],
          100: primary[100],
          200: primary[200],
          300: primary[300],
          400: primary[400],
          500: primary[500],
          600: primary[600],
          700: primary[700],
          800: primary[800],
          900: primary[900],
          DEFAULT: 'hsl(var(--primary))',
          foreground: 'hsl(var(--primary-foreground))',
        },

        // Gray - Warm neutrals
        gray: {
          50: gray[50],
          100: gray[100],
          200: gray[200],
          300: gray[300],
          400: gray[400],
          500: gray[500],
          600: gray[600],
          700: gray[700],
          800: gray[800],
          900: gray[900],
        },

        // Accent - Gold (rewards, premium, AI)
        accent: {
          50: accent[50],
          100: accent[100],
          200: accent[200],
          300: accent[300],
          400: accent[400],
          500: accent[500],
          600: accent[600],
          700: accent[700],
          800: accent[800],
          900: accent[900],
          DEFAULT: 'hsl(var(--accent))',
          foreground: 'hsl(var(--accent-foreground))',
        },

        // ========================================
        // Semantic Colors (status indicators)
        // ========================================

        // Emerald for success
        emerald: semantic.success,

        // Amber for warning
        amber: semantic.warning,

        // Red for danger/error
        red: semantic.danger,

        // Blue for info (use sparingly)
        blue: semantic.info,

        // ========================================
        // CSS Variable-based Colors (shadcn)
        // ========================================

        border: 'hsl(var(--border))',
        input: 'hsl(var(--input))',
        ring: 'hsl(var(--ring))',
        background: 'hsl(var(--background))',
        foreground: 'hsl(var(--foreground))',

        // Secondary - Subdued actions
        secondary: {
          DEFAULT: 'hsl(var(--secondary))',
          foreground: 'hsl(var(--secondary-foreground))',
        },

        // Destructive - Errors and deletions (maps to danger)
        destructive: {
          DEFAULT: 'hsl(var(--destructive))',
          foreground: 'hsl(var(--destructive-foreground))',
        },

        // Success - Positive actions
        success: {
          DEFAULT: 'hsl(var(--success))',
          foreground: 'hsl(var(--success-foreground))',
        },

        // Warning - Caution states
        warning: {
          DEFAULT: 'hsl(var(--warning))',
          foreground: 'hsl(var(--warning-foreground))',
        },

        // Info - Informational states
        info: {
          DEFAULT: 'hsl(var(--info))',
          foreground: 'hsl(var(--info-foreground))',
        },

        // Muted - Subdued elements
        muted: {
          DEFAULT: 'hsl(var(--muted))',
          foreground: 'hsl(var(--muted-foreground))',
        },

        // Popover - Floating elements
        popover: {
          DEFAULT: 'hsl(var(--popover))',
          foreground: 'hsl(var(--popover-foreground))',
        },

        // Card - Elevated surfaces
        card: {
          DEFAULT: 'hsl(var(--card))',
          foreground: 'hsl(var(--card-foreground))',
        },

        // Sidebar - Navigation
        sidebar: {
          DEFAULT: 'hsl(var(--sidebar))',
          foreground: 'hsl(var(--sidebar-foreground))',
          primary: 'hsl(var(--sidebar-primary))',
          'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
          accent: 'hsl(var(--sidebar-accent))',
          'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
          border: 'hsl(var(--sidebar-border))',
          ring: 'hsl(var(--sidebar-ring))',
        },

        // Chart colors for analytics
        chart: {
          '1': 'hsl(var(--chart-1))',
          '2': 'hsl(var(--chart-2))',
          '3': 'hsl(var(--chart-3))',
          '4': 'hsl(var(--chart-4))',
          '5': 'hsl(var(--chart-5))',
        },
      },

      borderRadius: {
        ...borderRadius,
        lg: 'var(--radius)',
        md: 'calc(var(--radius) - 2px)',
        sm: 'calc(var(--radius) - 4px)',
        // iOS-style rounded corners
        '2xl': '1rem', // 16px
        '3xl': '1.5rem', // 24px - iOS cards
      },

      boxShadow: {
        ...boxShadow,
        // Design System v2.0.0 elevation shadows
        'card-sm': '0 1px 3px 0 rgb(0 0 0 / 0.04), 0 1px 2px -1px rgb(0 0 0 / 0.04)',
        card: '0 2px 6px 0 rgb(0 0 0 / 0.05), 0 1px 3px -1px rgb(0 0 0 / 0.04)',
        'card-lg': '0 4px 12px 0 rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.04)',
        // iOS-style floating nav shadow
        float: '0 8px 32px -8px rgb(0 0 0 / 0.15), 0 4px 16px -4px rgb(0 0 0 / 0.1)',
        // Premium/gold glow for loyalty features
        'gold-glow': '0 0 20px 0 rgb(226 192 68 / 0.3)',
      },

      fontFamily: {
        // Primary font: Inter (Design System v2.0.0)
        sans: ['Inter', ...fontFamily.sans.split(', ').filter((f) => f !== 'Inter')],
        display: ['Inter', ...fontFamily.display.split(', ').filter((f) => f !== 'Inter')],
        mono: fontFamily.mono.split(', '),
      },

      fontSize: JSON.parse(JSON.stringify(fontSize)),
      spacing: { ...spacing },

      keyframes: {
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' },
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' },
        },
        'collapsible-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-collapsible-content-height)' },
        },
        'collapsible-up': {
          from: { height: 'var(--radix-collapsible-content-height)' },
          to: { height: '0' },
        },
        'fade-in': {
          from: { opacity: '0' },
          to: { opacity: '1' },
        },
        'fade-out': {
          from: { opacity: '1' },
          to: { opacity: '0' },
        },
        'slide-in-from-top': {
          from: { transform: 'translateY(-100%)' },
          to: { transform: 'translateY(0)' },
        },
        'slide-in-from-bottom': {
          from: { transform: 'translateY(100%)' },
          to: { transform: 'translateY(0)' },
        },
        'slide-in-from-left': {
          from: { transform: 'translateX(-100%)' },
          to: { transform: 'translateX(0)' },
        },
        'slide-in-from-right': {
          from: { transform: 'translateX(100%)' },
          to: { transform: 'translateX(0)' },
        },
        'caret-blink': {
          '0%,70%,100%': { opacity: '1' },
          '20%,50%': { opacity: '0' },
        },
        // New: hover scale animation
        'scale-up': {
          from: { transform: 'scale(1)' },
          to: { transform: 'scale(1.02)' },
        },
        'scale-down': {
          from: { transform: 'scale(1.02)' },
          to: { transform: 'scale(1)' },
        },
        // New: gold shimmer for premium elements
        shimmer: {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' },
        },
      },

      animation: {
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'collapsible-down': 'collapsible-down 0.2s ease-out',
        'collapsible-up': 'collapsible-up 0.2s ease-out',
        'fade-in': 'fade-in 0.15s ease-out',
        'fade-out': 'fade-out 0.15s ease-out',
        'slide-in-from-top': 'slide-in-from-top 0.2s ease-out',
        'slide-in-from-bottom': 'slide-in-from-bottom 0.2s ease-out',
        'slide-in-from-left': 'slide-in-from-left 0.2s ease-out',
        'slide-in-from-right': 'slide-in-from-right 0.2s ease-out',
        'caret-blink': 'caret-blink 1.25s ease-out infinite',
        // New animations
        'scale-up': 'scale-up 0.15s ease-out forwards',
        'scale-down': 'scale-down 0.15s ease-out forwards',
        shimmer: 'shimmer 2s linear infinite',
      },
    },
  },
};

// Legacy export for backwards compatibility
const preset = tailwindPreset;
export default preset;
